import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Slider,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useEffect, useState } from "react";
import { isEqual } from "lodash";
import { Transition } from "../../pages/app/utils/animation";
import { useModals } from "../../store/dialogs/hooks";

export const EditorDAWFineTuneDialog = ({
  fftOptionsDefault,
  fftSizeOptions,
  maxFrequency = 20000,
  minDistance = 1000,
  onApply,
  onCancel,
  spectrogramVariant,
  zoom = [0, 20000],
}) => {
  const { showEditorDAWFineTuneDialog } = useModals();
  const [zoomAmt, setZoomAmt] = useState(zoom);
  const hzBreakpoint = 1000;
  const textFieldWidth = "82px";
  // The variant the track is showing: the one last applied, if this track
  // offers it, otherwise the asset's default.
  const currentVariantId = fftSizeOptions.some(
    (option) => option.id === spectrogramVariant,
  )
    ? spectrogramVariant
    : (fftOptionsDefault?.id ?? "");
  const [selectedOption, setSelectedOption] = useState(currentVariantId);

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleReset = () => {
    setZoomAmt(zoom);
    setSelectedOption(currentVariantId);
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleZoomChange = (_event, value, activeThumb) => {
    if (activeThumb === 0) {
      setZoomAmt([Math.min(value[0], zoomAmt[1] - minDistance), zoomAmt[1]]);
    } else {
      setZoomAmt([zoomAmt[0], Math.max(value[1], zoomAmt[0] + minDistance)]);
    }
  };

  const handleApply = () => {
    const fftSize = fftSizeOptions.find(
      (option) => option.id === selectedOption,
    ).fftSize;
    onApply(zoomAmt, fftSize, selectedOption);
    handleReset();
  };

  const initialValue = {
    fft: currentVariantId,
    zoom: zoom,
  };

  const isUpdated = isEqual(initialValue, {
    fft: selectedOption,
    zoom: zoomAmt,
  });

  useEffect(() => {
    setSelectedOption(currentVariantId);
  }, [currentVariantId]);

  useEffect(() => {
    setZoomAmt(zoom);
  }, [zoom]);

  return (
    <Dialog
      aria-hidden={!showEditorDAWFineTuneDialog}
      closeAfterTransition={false}
      disableRestoreFocus
      onClose={handleCancel}
      open={showEditorDAWFineTuneDialog}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle>Fine Tune The Spectrogram</DialogTitle>
      <DialogContent dividers>
        <DialogContentText mb={3}>
          Regenerating the spectrogram can take up to a minute on large files.
        </DialogContentText>
        <Typography variant="overline">FFT Size [samples]</Typography>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
        >
          <FormControl fullWidth margin="normal">
            <InputLabel id="fft-size-id-label" sx={{ display: visuallyHidden }}>
              FFT Size [samples]
            </InputLabel>
            <Select
              displayEmpty
              labelId="fft-size-id-label"
              value={selectedOption}
              onChange={handleChange}
              id="fft-size"
            >
              {fftSizeOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {`${option.fftSize} (${option.windowMs} ms) ${option.overlap}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Box display="flex" mb={1} mt={1}>
          <Typography variant="overline">Frequency Range</Typography>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={3}
        >
          <TextField
            label={zoomAmt[0] < hzBreakpoint ? "Hz" : "kHz"}
            value={zoomAmt[0] < hzBreakpoint ? zoomAmt[0] : zoomAmt[0] / 1000}
            variant="outlined"
            size="small"
            slotProps={{ htmlInput: { readOnly: true } }}
            sx={{ width: textFieldWidth }}
          />
          <Box display="flex" flex={1}>
            <Slider
              value={zoomAmt}
              min={0}
              max={maxFrequency}
              step={100}
              onChange={handleZoomChange}
              valueLabelDisplay="off"
            />
          </Box>
          <TextField
            label={zoomAmt[1] < hzBreakpoint ? "Hz" : "kHz"}
            slotProps={{ htmlInput: { readOnly: true } }}
            value={zoomAmt[1] < hzBreakpoint ? zoomAmt[1] : zoomAmt[1] / 1000}
            variant="outlined"
            size="small"
            sx={{ width: textFieldWidth }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mt: 1 }}>
        <Button variant="outlined" onClick={handleCancel} sx={{ mr: "auto" }}>
          Cancel Changes
        </Button>
        <Button disabled={isUpdated} variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button disabled={isUpdated} variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
